import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import Quill from 'quill';
import Image from 'quill/formats/image';
import QuillResizeImage from 'quill-resize-image';
import { Box } from '@mui/material';
import 'quill/dist/quill.snow.css';

class CustomImage extends Image {
  static create(value) {
    const node = super.create(value);
    if (typeof value === 'string') {
      node.setAttribute('src', value);
      node.setAttribute('width', '100%');
    }
    return node;
  }
}

CustomImage.blotName = 'customImage';
CustomImage.tagName = 'img';

Quill.register('formats/customImage', CustomImage);
Quill.register('modules/resize', QuillResizeImage);

const AlignStyle = Quill.import('attributors/style/align');
Quill.register(AlignStyle, true);

const QuillEditor = forwardRef(
  (
    {
      value,
      onTextChange,
      modules,
      styles,
      className = '',
      placeholder = '',
      onBlur = () => {},
      imageUpload = () => {},
    },
    ref
  ) => {
    const containerRef = useRef(null);
    const onTextChangeRef = useRef(onTextChange);
    const isInternalChange = useRef(false);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
    });

    useEffect(() => {
      const container = containerRef.current;
      const editorContainer = container.appendChild(container.ownerDocument.createElement('div'));

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        placeholder,
        modules,
      });

      ref.current = quill;

      if (value) {
        quill.root.innerHTML = value;
      }

      // Drag and drop support
      quill.root.addEventListener(
        'drop',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) {
            const file = event.dataTransfer.files[0];

            if (/^image\//.test(file.type)) {
              imageUpload(file);
            }
          }
        },
        true // Ensures the handler runs before Quill does anything.
      );

      quill.root.addEventListener('dragover', (event) => {
        event.preventDefault();
      });

      // Stops blur being triggered when clicking the toolbar
      document.querySelectorAll('.ql-toolbar').forEach((toolbar) => {
        toolbar.addEventListener('mousedown', (e) => e.preventDefault());
      });

      // Detect changes
      quill.on(Quill.events.TEXT_CHANGE, () => {
        isInternalChange.current = true;

        const content = quill.root.innerHTML;

        onTextChangeRef.current(content);
      });

      quill.root.addEventListener('blur', () => {
        const content = quill.root.innerHTML;
        onBlur(content);
      });

      return () => {
        ref.current = null;
        container.innerHTML = '';
      };
    }, [ref]);

    useEffect(() => {
      if (isInternalChange.current) {
        isInternalChange.current = false;

        return;
      }

      const quill = ref.current;

      if (quill && value != null && quill.root.innerHTML !== value) {
        quill.root.innerHTML = value;
      }
    }, [value, ref]);

    return <Box className={className} sx={styles} ref={containerRef} />;
  }
);

QuillEditor.displayName = 'Editor';

export default QuillEditor;
